import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Nav } from "./components/eskale/Nav";
import { Reveal } from "./components/eskale/Reveal";
import { WhatsAppFloating, buildWhatsAppLink } from "./components/eskale/WhatsAppButton";
import { BookingModal, UNITS } from "./components/eskale/BookingModal";
import { Reviews } from "./components/eskale/Reviews";
import { LongStay } from "./components/eskale/LongStay";
import { LegalPages } from "./components/eskale/LegalPages";
import { useLang } from "./i18n";
import logo from "./assets/logo-eskale.png";
import heroVideo from "./assets/hero-video.mp4";
import t1Main from "./assets/t1-main.jpg";
import t1Photo1 from "./assets/t1-1.jpg";
import t1Photo2 from "./assets/t1-2.jpg";
import t1Photo3 from "./assets/t1-3.jpg";
import t1Photo4 from "./assets/t1-4.jpg";
import t1Photo5 from "./assets/t1-5.jpg";
import t1Photo6 from "./assets/t1-6.jpg";
import t1Photo7 from "./assets/t1-7.jpg";
import t1Photo8 from "./assets/t1-8.jpg";
import suiteT2Img from "./assets/suite-t2.jpg";
import t2Photo1 from "./assets/t2-1.jpg";
import t2Photo2 from "./assets/t2-2.jpg";
import t2Photo3 from "./assets/t2-3.jpg";
import t2Photo4 from "./assets/t2-4.jpg";
import t2Photo5 from "./assets/t2-5.jpg";
import suiteT3Img from "./assets/suite-t3.jpg";
import t3Photo1 from "./assets/t3-1.jpg";
import t3Photo2 from "./assets/t3-2.jpg";
import t3Photo3 from "./assets/t3-3.jpg";
import t3Photo4 from "./assets/t3-4.jpg";
import t3Photo5 from "./assets/t3-5.jpg";
import suiteVillaImg from "./assets/suite-villa.jpg";
import villaEntry from "./assets/villa-entry.jpg";
import villaPhoto1 from "./assets/villa-1.jpg";
import villaPhoto2 from "./assets/villa-2.jpg";
import villaPhoto3 from "./assets/villa-3.jpg";
import villaPhoto4 from "./assets/villa-4.jpg";
import villaPhoto5 from "./assets/villa-5.jpg";
import reserverBg from "./assets/reserver-bg.jpg";
import directBg from "./assets/direct-bg.jpg";
import equipPool from "./assets/equip-pool.jpg";
import equipGym from "./assets/equip-gym.jpg";
import equipEntrance from "./assets/equip-entrance.jpg";
import equipLounge from "./assets/equip-lounge.jpg";
import equipParking from "./assets/equip-parking.jpg";
import "./App.css";

function App() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preselected, setPreselected] = useState(undefined);
  const [legalPage, setLegalPage] = useState(null);
  const { lang, t } = useLang();

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.seo.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t.seo.description);
  }, [lang, t]);

  function openBooking(unit) {
    setPreselected(unit);
    setBookingOpen(true);
  }

  return (
    <main id="top" className="relative min-h-screen bg-background text-foreground">
      <Nav onBook={() => openBooking()} />
      <Hero onBook={() => openBooking()} />
      <Pillars />
      <Accommodations />
      <Reviews />
      <Direct />
      <Services />
      <Equipments />
      <Testimonial />
      <Upsells onBook={() => openBooking()} />
      <LongStay />
      <Practical />
      <FinalCta onBook={() => openBooking()} />
      <Footer onLegal={setLegalPage} />
      <WhatsAppFloating />
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} initialUnit={preselected} />
      <LegalPages page={legalPage} onClose={() => setLegalPage(null)} />
    </main>
  );
}

/* ──────────── HERO ──────────── */
function Hero({ onBook }) {
  const { t } = useLang();
  return (
    <section className="relative isolate min-h-screen overflow-hidden">
      <video
        src={heroVideo}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label="Vidéo de L'Eskale Guyane à Rémire-Montjoly"
        className="pointer-events-none absolute left-1/2 top-1/2 min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/55 to-ink/80" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-6 pt-28 pb-20 text-center text-ivory lg:px-10">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.4em] text-gold">{t.hero.eyebrow}</p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-6 font-display text-6xl leading-[1.05] sm:text-7xl lg:text-[7rem]">
            L&rsquo;Eskale <span className="italic text-gold">Guyane</span>
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 max-w-xl font-display italic text-2xl text-ivory/90 sm:text-3xl">{t.hero.tagline}</p>
        </Reveal>

        <Reveal delay={420}>
          <button
            type="button"
            onClick={onBook}
            className="mt-12 inline-flex items-center bg-gold px-10 py-5 text-xs uppercase tracking-[0.28em] text-primary-foreground shadow-lg transition-all hover:brightness-95"
          >
            {t.hero.cta}
          </button>
        </Reveal>

        <Reveal delay={560}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-ivory/75">
            <span>
              <span className="text-gold">★</span> 8.7/10 Booking.com
            </span>
            <span className="text-gold">·</span>
            <span>
              <span className="text-gold">★</span> 4.4/5 Google
            </span>
            <span className="text-gold">·</span>
            <span>{t.hero.tripadvisor}</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ──────────── PILLARS ──────────── */
function Pillars() {
  const { t } = useLang();
  return (
    <section id="piliers" className="px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-6xl">
        <SectionHeader eyebrow={t.pillars.eyebrow} title={t.pillars.title} />
        <div className="mt-16 grid gap-px border border-border bg-border md:grid-cols-3">
          {t.pillars.items.map((p, i) => (
            <Reveal key={p.n} delay={i * 100}>
              <div className="column-accent h-full bg-background p-10 pl-12">
                <span className="font-display text-5xl italic text-crimson">{p.n}</span>
                <div className="hairline mt-4 w-12" />
                <h3 className="mt-6 font-display text-3xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────── ACCOMMODATIONS WITH CAROUSEL ──────────── */
const CARDS = [
  {
    id: "T1",
    price: "90 €",
    gradient: "gradient-suite-a",
    images: [t1Main, t1Photo3, t1Photo4, t1Photo5, t1Photo6, t1Photo7, t1Photo8, t1Photo1, t1Photo2],
  },
  {
    id: "T2",
    price: "120 €",
    gradient: "gradient-suite-b",
    images: [suiteT2Img, t2Photo1, t2Photo3, t2Photo4, t2Photo2, t2Photo5],
    badge: true,
  },
  {
    id: "T3",
    price: "180 €",
    gradient: "gradient-suite-c",
    images: [suiteT3Img, t3Photo1, t3Photo2, t3Photo3, t3Photo4, t3Photo5],
  },
  {
    id: "VILLA",
    price: "350 €",
    gradient: "gradient-suite-d",
    images: [villaEntry, suiteVillaImg, villaPhoto1, villaPhoto2, villaPhoto3, villaPhoto4, villaPhoto5],
  },
];

/* ──────────── UNIT GALLERY WITH TOUCH SWIPE ──────────── */
function UnitGallery({ images, alt, gradient, badge, overlay }) {
  const [index, setIndex] = useState(0);
  const count = images.length;
  const hasMultiple = count > 1;
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    if (index > count - 1) setIndex(0);
  }, [count, index]);

  const go = (next) => setIndex(((next % count) + count) % count);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!hasMultiple) return;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        go(index + 1);
      } else {
        go(index - 1);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative aspect-[16/10] ${gradient} overflow-hidden touch-pan-y`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {count > 0 && (
        <div
          className="absolute inset-0 flex h-full w-full transition-transform duration-700 ease-[cubic-bezier(.2,.7,.2,1)]"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${alt} — photo ${i + 1}`}
              loading="lazy"
              className="h-full w-full shrink-0 object-cover"
              draggable={false}
            />
          ))}
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />

      {badge && (
        <span className="absolute left-5 top-5 z-10 bg-crimson px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-ivory">
          {badge}
        </span>
      )}

      {hasMultiple && (
        <>
          <button
            type="button"
            aria-label="Photo précédente"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-ivory/30 bg-ink/40 text-ivory opacity-0 backdrop-blur-sm transition hover:bg-ink/70 hover:text-gold focus:opacity-100 group-hover:opacity-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Photo suivante"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-ivory/30 bg-ink/40 text-ivory opacity-0 backdrop-blur-sm transition hover:bg-ink/70 hover:text-gold focus:opacity-100 group-hover:opacity-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Aller à la photo ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-5 bg-gold" : "w-1.5 bg-ivory/50 hover:bg-ivory/80"
                }`}
              />
            ))}
          </div>
        </>
      )}

      <div className="absolute inset-0 z-[5] flex flex-col justify-end">{overlay}</div>
    </div>
  );
}

function Accommodations() {
  const { t } = useLang();
  return (
    <section id="logements" className="border-t border-border bg-secondary/40 px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <SectionHeader eyebrow={t.acc.eyebrow} title={t.acc.title} />
        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {CARDS.map((c, i) => {
            const u = UNITS[c.id];
            const card = t.acc.cards[c.id];
            const name = t.units[c.id];
            return (
              <Reveal key={c.id} delay={i * 100} as="article">
                <div className="group flex h-full flex-col border border-border bg-background transition hover:border-gold/60">
                  <UnitGallery
                    images={c.images}
                    alt={name}
                    gradient={c.gradient}
                    badge={c.badge ? t.acc.badge : undefined}
                    overlay={
                      <div className="p-6 text-ivory">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                          {u.surface} · {u.capacity} {t.acc.pers}
                        </p>
                        <h3 className="mt-2 font-display text-3xl italic">{name}</h3>
                      </div>
                    }
                  />

                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-sm text-muted-foreground">{card.tagline}</p>
                    <div className="column-accent mt-5 border-y border-border py-4 pl-4">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-crimson">{card.highlight}</p>
                      <p className="mt-1 text-sm text-foreground/75" data-testid={`unit-price-${c.id.toLowerCase()}`}>
                        {t.acc.from} <strong className="font-semibold text-foreground">{c.price}</strong> {t.acc.perNight}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">{t.acc.breakfast}</p>
                    </div>
                    <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-foreground/80">
                      {card.amenities.map((a) => (
                        <li key={a} className="flex items-start gap-2">
                          <span className="mt-2 h-px w-3 shrink-0 bg-gold" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={buildWhatsAppLink(t.wa.units[c.id])}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${t.acc.book} — ${name}`}
                      data-testid={`book-unit-${c.id.toLowerCase()}-whatsapp`}
                      className="mt-7 inline-flex items-center justify-center border border-foreground py-4 text-[11px] uppercase tracking-[0.25em] transition hover:bg-foreground hover:text-background"
                    >
                      {t.acc.book}
                    </a>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={200}>
          <p className="mt-10 text-center text-xs text-muted-foreground" data-testid="pricing-conditions-note">
            {t.acc.conditions}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ──────────── DIRECT BOOKING ──────────── */
function Direct() {
  const { t } = useLang();
  return (
    <section id="direct" className="relative overflow-hidden bg-ink px-6 py-28 text-ivory lg:px-10 lg:py-36">
      <img src={directBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/35 via-ink/25 to-ink/55" />
      <div className="relative z-10 mx-auto max-w-5xl">
        <Reveal>
          <div className="mx-auto max-w-2xl rounded-2xl bg-ink/60 px-8 py-8 backdrop-blur-sm sm:px-12 sm:py-10">
            <p className="text-center text-[11px] uppercase tracking-[0.3em] text-gold">{t.direct.eyebrow}</p>
            <h2 className="mt-4 text-center font-display text-4xl italic sm:text-5xl">{t.direct.title}</h2>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-14 overflow-hidden rounded-2xl border border-ivory/15 bg-ink/60 backdrop-blur-sm">
            <div className="grid grid-cols-3 bg-ivory/5 text-[10px] uppercase tracking-[0.2em] text-ivory/70">
              <div className="p-4">{t.direct.colAdvantage}</div>
              <div className="p-4 text-gold">{t.direct.colDirect}</div>
              <div className="p-4">{t.direct.colOta}</div>
            </div>
            {t.direct.rows.map((r, i) => (
              <div key={i} className="grid grid-cols-3 border-t border-ivory/10 text-sm">
                <div className="p-4 text-ivory/80">{r[0]}</div>
                <div className="p-4 text-gold">{r[1]}</div>
                <div className="p-4 text-ivory/60">{r[2]}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ──────────── SERVICES ──────────── */
function Services() {
  const { t } = useLang();
  return (
    <section id="services" className="px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <SectionHeader eyebrow={t.services.eyebrow} title={t.services.title} />
        <div className="mt-16 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {t.services.items.map((s, i) => (
            <Reveal key={s.n} delay={i * 60}>
              <div className="h-full bg-background p-8">
                <span className="font-display text-3xl italic text-gold/70">{s.n}</span>
                <h3 className="mt-4 font-display text-xl">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────── EQUIPMENTS GALLERY ──────────── */
const EQUIP_PHOTOS = [
  { key: "pool", src: equipPool, wide: true },
  { key: "gym", src: equipGym, wide: true },
  { key: "entrance", src: equipEntrance },
  { key: "lounge", src: equipLounge },
  { key: "parking", src: equipParking },
];

function Equipments() {
  const { t } = useLang();
  return (
    <section id="espaces" className="border-t border-border bg-secondary/40 px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-6xl">
        <SectionHeader eyebrow={t.equip.eyebrow} title={t.equip.title} />
        <div className="mt-16 grid gap-6 sm:grid-cols-2">
          {EQUIP_PHOTOS.filter((p) => p.wide).map((p, i) => (
            <Reveal key={p.key} delay={i * 100}>
              <figure className="group overflow-hidden border border-border bg-background" data-testid={`equip-photo-${p.key}`}>
                <div className="overflow-hidden">
                  <img
                    src={p.src}
                    alt={t.equip.captions[p.key]}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="flex items-center gap-3 px-5 py-4">
                  <span className="h-px w-6 bg-gold" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-foreground/80">{t.equip.captions[p.key]}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid gap-6 sm:grid-cols-3">
          {EQUIP_PHOTOS.filter((p) => !p.wide).map((p, i) => (
            <Reveal key={p.key} delay={i * 100}>
              <figure className="group overflow-hidden border border-border bg-background" data-testid={`equip-photo-${p.key}`}>
                <div className="overflow-hidden">
                  <img
                    src={p.src}
                    alt={t.equip.captions[p.key]}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="flex items-center gap-3 px-5 py-4">
                  <span className="h-px w-6 bg-gold" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-foreground/80">{t.equip.captions[p.key]}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────── TESTIMONIAL ──────────── */
function Testimonial() {
  const { t } = useLang();
  return (
    <section className="bg-secondary/50 px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <span className="font-display text-7xl italic leading-none text-crimson">&ldquo;</span>
          <p className="mt-2 font-display text-2xl italic leading-relaxed text-foreground sm:text-3xl">{t.testimonial.text}</p>
          <div className="hairline mx-auto mt-10 w-16" />
          <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-muted-foreground">{t.testimonial.author}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ──────────── UPSELLS ──────────── */
function Upsells({ onBook }) {
  const { t } = useLang();
  return (
    <section className="px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-6xl">
        <SectionHeader eyebrow={t.upsells.eyebrow} title={t.upsells.title} />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {t.upsells.items.map((it, i) => (
            <Reveal key={it.title} delay={i * 100}>
              <div className="column-accent flex h-full flex-col border border-border bg-card p-7 pl-9">
                <p className="text-[10px] uppercase tracking-[0.22em] text-crimson">{it.price}</p>
                <h3 className="mt-3 font-display text-2xl">{it.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{it.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button
            onClick={onBook}
            className="inline-flex items-center border border-foreground px-8 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-foreground hover:text-background"
          >
            {t.upsells.cta}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ──────────── PRACTICAL ──────────── */
function Practical() {
  const { t } = useLang();
  return (
    <section id="infos" className="bg-secondary/40 px-6 py-28 lg:px-10 lg:py-36">
      <div className="mx-auto max-w-5xl">
        <SectionHeader eyebrow={t.practical.eyebrow} title={t.practical.title} />
        <div className="mt-14 grid gap-px border border-border bg-border sm:grid-cols-2">
          {t.practical.items.map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1 bg-background p-6">
              <span className="text-[10px] uppercase tracking-[0.22em] text-gold">{k}</span>
              <span className="text-sm text-foreground/85">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────── FINAL CTA ──────────── */
function FinalCta({ onBook }) {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-ink px-6 py-32 text-ivory lg:px-10 lg:py-40">
      <img src={reserverBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/75 to-ink/90" />
      <div className="relative z-10 mx-auto max-w-3xl text-center">
        <Reveal>
          <h2 className="font-display text-5xl italic sm:text-6xl">{t.finalCta.title}</h2>
          <p className="mt-6 text-lg text-ivory/80">{t.finalCta.text}</p>
          <button
            type="button"
            onClick={onBook}
            className="mt-12 inline-flex items-center bg-gold px-10 py-5 text-xs uppercase tracking-[0.28em] text-primary-foreground shadow-lg transition hover:brightness-95"
          >
            {t.finalCta.cta}
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ──────────── FOOTER ──────────── */
function Footer({ onLegal }) {
  const { t } = useLang();
  return (
    <footer className="border-t border-border bg-background px-6 py-16 lg:px-10">
      <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-3">
            <img src={logo} alt="L'Eskale Guyane" className="h-12 w-12 object-contain" />
            <p className="font-display text-2xl italic">L&rsquo;Eskale Guyane</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            20 Chemin Germain
            <br />
            97354 Rémire-Montjoly
            <br />
            Guyane française
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{t.footer.contact}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <a href="tel:+594694449985" className="hover:text-gold">
                +594 694 44 99 85
              </a>
            </li>
            <li>
              <a href="https://leskaleguyane.net" className="hover:text-gold">
                leskaleguyane.net
              </a>
            </li>
            <li className="text-muted-foreground">{t.footer.booking}</li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{t.footer.legal}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <button type="button" onClick={() => onLegal("mentions")} data-testid="footer-legal-mentions" className="hover:text-gold">
                {t.footer.mentions}
              </button>
            </li>
            <li>
              <button type="button" onClick={() => onLegal("confidentialite")} data-testid="footer-legal-confidentialite" className="hover:text-gold">
                {t.footer.privacy}
              </button>
            </li>
            <li>
              <button type="button" onClick={() => onLegal("cookies")} data-testid="footer-legal-cookies" className="hover:text-gold">
                {t.footer.cookies}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-12 max-w-7xl border-t border-border pt-6 text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        © {new Date().getFullYear()} L&rsquo;Eskale Guyane — {t.footer.rights}
      </div>
    </footer>
  );
}

/* ──────────── SHARED ──────────── */
function SectionHeader({ eyebrow, title }) {
  return (
    <div className="text-center">
      <Reveal>
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="mt-4 font-display text-4xl italic sm:text-5xl">{title}</h2>
      </Reveal>
      <Reveal delay={180}>
        <div className="hairline mx-auto mt-6 w-16" />
      </Reveal>
    </div>
  );
}

export default App;
